import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard, FolderKanban, FileCheck2, ScrollText, Image as ImageIcon,
  Sparkles, BookOpen, LogOut, Search, Send, Upload, ShieldCheck, AlertTriangle,
  CheckCircle2, ChevronRight, Building2, Hash, BarChart3, MessageSquareText, Loader2, X,
  Inbox, Link as LinkIcon, Menu, LogIn, FileText
} from "lucide-react";
import { API } from "../lib/api";
import { fmtAUD, fmtMoney, fmtBytes } from "../lib/format";
import { WorkflowNSWRABenefits } from "./WorkflowEPARScope";

/* ---------- Workflow presentation — EW Finalisation ---------- */
export function WorkflowEWFinalisation() {
  const steps = [
    {
      n: 1, title: "Ingest the EW Finalisation package",
      sub: "One ZIP from SmartyGrants → fully parsed, indexed, classified",
      body: "Drag a SmartyGrants finalisation ZIP into the Ingest Inbox (or paste a Google Drive link). RAAgent unpacks nested ZIPs (Post-Event, Completion, Pre-Event evidence archives), classifies every file (application PDF, ledger, photos, CET workbook, certificates), and detects the portal type with 95-100% confidence. If the package only contains the SmartyGrants application PDF (no structured spreadsheet), the assessor can one-click extract defects via Claude Sonnet 4.5.",
      tech: "Async ingest · nested ZIP extraction · multi-portal SG parser (EW / IRW / EPAR Assessment Spreadsheets via single flexible parser) · AI fallback for PDF-only packages",
    },
    {
      n: 2, title: "Sortable file tree — every artefact at one click",
      sub: "No more digging through the source ZIP externally",
      body: "Every file in the package surfaces in a sortable, filterable file tree on the job page. Filter chips: Claim forms · CET · Slip · Ledger · Photos · Evidence ZIPs · PDFs. Sort by name / classification / size / type. Search any path. Click Open on any row to stream the file inline (PDFs/images) or download (Excel/ZIP). The tree is snapshotted to Mongo so it survives disk pruning.",
      tech: "files_seen classification · /source-tree + /source-file endpoints · path-traversal-safe streaming · disk-pruning resilient",
    },
    {
      n: 3, title: "Auto-load ledger + photo defensibility check",
      sub: "Council's ledger is already in the package — load it in one click",
      body: "The ingest classifier identifies the council's financial summary inside the ZIP. A green 'Ledger detected in package · Auto-load' card appears on the job page — one click parses the ledger into the assessor view. Photos auto-bucket into damage / completion / pre across all DM groups (with self-healing re-walk if photos are added later).",
      tech: "Auto-detect + one-click parse · Post-Event/Completion/Pre-Event folder convention recognition · self-healing photo re-walk on first job open",
    },
    {
      n: 4, title: "Ledger reconciliation — strip + re-apply on-costs",
      sub: "Eligibility classification + Fact Sheet rate, then the assessor's verdict",
      body: "Every ledger line is auto-classified Eligible / Ineligible / Review against NSWRA DRFA rules. Submitted on-cost lines are STRIPPED. On-costs are re-applied at the published Fact Sheet rate against ELIGIBLE labour only. The assessor then has full per-line override: ELG / INEL / REV verdict buttons, tri-state on-cost toggle (force ON / strip OFF / use tool default), and a free-text override reason that flows into the audit trail. Bulk-select multiple lines to mark or strip in batches. Totals recompute instantly.",
      tech: "iter_rows streaming parser (15k rows in 1.8s) · client-side total recomputation · debounced backend persistence · 6-column audit-trail download (with amber highlighting on overridden rows)",
    },
    {
      n: 5, title: "Per-defect decisions + council certification",
      sub: "YES / RFI / NO per defect, plus D / C / S sign-off",
      body: "The assessor walks each defect with YES (substantiated) / RFI (cannot substantiate) / NO (ineligible) decisions plus the council's Damage / Costs / Substantial-completion certification boxes. The progress chip shows readiness in real time. Single-claim view — no DMs, no CET (EW is actuals-based by definition).",
      tech: "Per-defect decision persistence · D/C/S certification capture · readiness chip · works identically for IRW Finalisation packages",
    },
    {
      n: 6, title: "EW Assessment Spreadsheet + assessed ledger output",
      sub: "Drop straight into SharePoint",
      body: "RAAgent emits two artefacts: (a) the assessed ledger .xlsx in the EXACT format the council submitted, with 6 decision columns prepended (Eligibility, Category, On-cost AUD, Assessor Verdict, On-cost Applied?, Override Reason) and colour-coded highlighting; (b) the populated EW Assessment Spreadsheet ready for upload back to SmartyGrants. Both reflect the assessor's overrides and per-defect decisions.",
      tech: "Original ledger format preserved · openpyxl write_only mode (15k rows in 4.8s) · NSWRA Cat B folder spec · ready for SharePoint sync",
    },
  ];
  return (
    <div className="space-y-8" data-testid="workflow-ew-finalisation-page">
      <div className="border-b border-slate-200 pb-6">
        <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-emerald-700 mb-2">Workflow walkthrough</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">EW Finalisation</h1>
        <p className="text-base text-slate-600 mt-2 max-w-3xl leading-relaxed">
          How RAAgent reconciles a council&apos;s actual Emergency Works costs against the
          NSWRA DRFA eligibility rules and the published Employee On-Costs Fact Sheet —
          in six steps, producing both the assessed ledger and the Verified Package output.
        </p>
        <div className="mt-4 flex flex-wrap gap-3 text-[12px]">
          <Link to="/portal/EW%20Finalisation" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-700" data-testid="workflow-ew-cta-portal">
            <FolderKanban className="w-3.5 h-3.5"/> Open EW Finalisation portal
          </Link>
          <Link to="/tools/ledger-checker" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">
            <ScrollText className="w-3.5 h-3.5"/> Try the Ledger Checker
          </Link>
          <Link to="/tools/photo-checker" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">
            <ImageIcon className="w-3.5 h-3.5"/> Try the Photo Checker
          </Link>
          <Link to="/workflow/epar-scope" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">
            <BookOpen className="w-3.5 h-3.5"/> EPAR Scope workflow
          </Link>
        </div>
      </div>

      <WorkflowNSWRABenefits />

      <div className="space-y-5">
        {steps.map((s) => (
          <div key={s.n} className="card p-6 flex gap-5" data-testid={`workflow-ew-step-${s.n}`}>
            <div className="shrink-0 w-14 h-14 rounded-full bg-emerald-600 text-white flex items-center justify-center font-heading font-bold text-2xl">
              {s.n}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500 mb-0.5">Step {s.n}</div>
              <h2 className="font-heading text-xl font-semibold leading-tight">{s.title}</h2>
              <div className="text-sm text-slate-500 italic mt-0.5">{s.sub}</div>
              <p className="text-[13.5px] text-slate-700 mt-3 leading-relaxed">{s.body}</p>
              <div className="mt-3 text-[11px] text-slate-500 font-mono border-l-2 border-slate-200 pl-3">
                {s.tech}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card p-6 bg-emerald-50 border-emerald-200">
        <h3 className="font-heading text-lg font-semibold mb-2">Why the on-cost rule matters</h3>
        <p className="text-[13px] text-slate-700 leading-relaxed">
          Councils often submit ledgers with on-costs already loaded onto labour lines — sometimes at
          a council-internal rate (e.g. 38%) that differs from the published NSWRA Fact Sheet rate.
          RAAgent <span className="font-semibold">strips</span> those submitted on-cost lines (visible
          in the assessed ledger as blue, line-through rows) and re-applies on-costs against
          eligible labour at the published rate. This produces a defensible, audit-ready ledger
          that the council and NSWRA can both reconcile to the same number.
        </p>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
          {["FY22-23 · 32.677%", "FY23-24 · 32.891%", "FY24-25 · 33.463%", "FY25-26 · 34.232%"].map((s) => (
            <div key={s} className="bg-white border border-emerald-200 rounded p-2 font-mono">{s}</div>
          ))}
        </div>
        <div className="text-[10.5px] text-slate-500 mt-2">Councils ordinary-hours rates per the NSW RA Essential Public Assets Employee On-Costs Fact Sheet.</div>
      </div>
    </div>
  );
}
